import axios from "axios"
import queryString from "query-string"
import lodash from "lodash"
import AppService from "@/AppService"
import * as utils from "@/Shared/utils"
import * as appDataService from "@/AppDataService"
import {
  API_MPX_SIGNIN,
  API_MPX_SIGNOUT,
  API_MPX_AUTHORIZE,
  SSO_AUTHORIZATION,
  API_MPX_REGISTRY,
  SSO_TOKEN,
  SSO_API_TOKEN,
  SSO_USER_INFO,
  API_MPX_ACCOUNT
} from "./urls"

/**
 * mpxSignIn - Retrieve provider groups
 * @param {string} userName
 * @param {string} password
 * @returns {Promise}
 */
export function mpxSignIn(userName, password) {
  let signInURL = utils.getServerUrl() + API_MPX_SIGNIN
  return axios
    .get(signInURL, {
      auth: {
        username: "mpx/" + userName, // NOTE: appending 'mpx/' is required for MPX login
        password: password
      }
    })
    .then((response) => {
      return response.data.signInResponse
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * mpxSignOut - Retrieve provider groups
 * @returns {Promise}
 */
export function mpxSignOut() {
  return appDataService
    .getMPXUserInfo()
    .then((userInfo) => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      let signOutURL = utils.getServerUrl() + API_MPX_SIGNOUT
      signOutURL += "?" + queryString.stringify(queryObject)
      return axios
        .get(signOutURL)
        .then((response) => {
          return response
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * mpxRegistry - Retrieve service registry
 * @param {string} pid
 * @returns {Promise}
 */
export function mpxRegistry(pid) {
  return appDataService
    .getMPXUserInfo()
    .then((userInfo) => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["byPid"] = pid
      let registryURL = utils.getServerUrl() + API_MPX_REGISTRY
      registryURL += "?" + queryString.stringify(queryObject)
      return axios
        .get(registryURL)
        .then((response) => {
          return response
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * mpxRegistry - Retrieve service registry
 * @param {string} pid
 * @returns {Promise}
 */
export function mpxSubAccounts(parentAccountId) {
  return appDataService
    .getMPXUserInfo()
    .then((userInfo) => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["byOwnerId"] = parentAccountId
      let registryURL = utils.getServerUrl() + API_MPX_ACCOUNT
      registryURL += "?" + queryString.stringify(queryObject)
      return axios
        .get(registryURL)
        .then((response) => {
          return response
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * mpxAuthorize - Retrieve provider groups
 * @param {string} tokenInput
 * @param {string} service
 * @returns {Promise}
 */
export function mpxAuthorize(tokenInput, service) {
  let tokenPromise = {}
  // Retrieve token if not provided
  if (!lodash.isUndefined(tokenInput)) {
    tokenPromise = new Promise(function (resolve) {
      resolve(tokenInput)
    })
  } else {
    tokenPromise = appDataService
      .getMPXUserInfo()
      .then((userInfo) => {
        return userInfo.token
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  return tokenPromise.then((token) => {
    let queryObject = {}
    queryObject["token"] = token
    queryObject["service"] = service
    let authorizeURL = utils.getServerUrl() + API_MPX_AUTHORIZE
    authorizeURL += "?" + queryString.stringify(queryObject)
    return axios
      .get(authorizeURL)
      .then((response) => {
        if (!lodash.isUndefined(response.data.authorizeResponse)) {
          let authorizeResponse = response.data.authorizeResponse
          let accountsList = authorizeResponse.accounts
          // List of parent accounts
          let parentAccounts = lodash.filter(accountsList, (account) => {
            return account.parentAccountIds.length === 0
          })
          // Associate the service that each of the accounts have
          lodash.forEach(parentAccounts, (account) => {
            if (service === AppService.getMvatsServiceString()) {
              account["_service"] = [AppService.getMvatsServiceString()]
            } else if (service === AppService.getOpsUIServiceString()) {
              account["_service"] = [AppService.getOpsUIServiceString()]
            }
          })
          // Discard all the accounts expect parents
          response.data.authorizeResponse.accounts = parentAccounts
          return response.data.authorizeResponse
        } else {
          // Return empty as we don't have 'authorizeResposne', this could be an
          // exception but we don't need to specifically handle that here
          return {}
        }
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  })
}

/**
 * getWebSecUrl - Get end point to retrieve temporary authoriztion code for SSO
 * @return {Promise}
 */
export function getWebSecUrl() {
  let authorizationURL = utils.getServerUrl() + SSO_AUTHORIZATION
  return axios
    .get(authorizationURL)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * getSSOToken - Retrieve Access Token and ID Token
 * @param  {string} authCode temporary authorization code
 * @return {Promise}
 */
export function getSSOToken(authCode) {
  let ssoTokenURL = utils.getServerUrl() + SSO_TOKEN
  ssoTokenURL +=
    "?" +
    queryString.stringify({
      code: authCode
    })
  return axios
    .get(ssoTokenURL)
    .then((response) => {
      return response
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * getSSOAPIToken - Retrieve Access Token and ID Token
 * @param  {string} authCode temporary authorization code
 * @return {Promise}
 */
export function getSSOAPIToken(idToken, refreshToken) {
  let ssoTokenURL = utils.getServerUrl() + SSO_API_TOKEN
  ssoTokenURL +=
    "?" +
    queryString.stringify({
      idToken: idToken,
      refreshToken: refreshToken
    })
  return axios
    .get(ssoTokenURL)
    .then((response) => {
      return response
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * getSSOUserInfo - Retrieve User Info
 * @param  {type} accessToken
 * @return {Promise}
 */
export function getSSOUserInfo(accessToken) {
  let ssoUserInfoURL = utils.getServerUrl() + SSO_USER_INFO
  ssoUserInfoURL +=
    "?" +
    queryString.stringify({
      accessToken: accessToken
    })
  return axios
    .get(ssoUserInfoURL)
    .then((response) => {
      return response
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}



// WEBPACK FOOTER //
// ./src/Auth/AuthDataService.js